import type React from 'react';
import { CompRef } from '@wix/document-services-types';
import { ClientSpecMapService } from '../sharedServices';
import type { EditorPointer } from '@wix/editor-document-logic-api';

export class PanelStore {
  /**
   * we store panel factories instead of a React component,
   * since panels are stored per type,
   * but opened per component (editor pointer), so we build a panel for a specific component
   */
  #store = new Map<
    string,
    Record<string, (editorPointer?: EditorPointer<CompRef>) => React.ReactNode>
  >();

  static getPanelId = (compType: string, path: string[]) => {
    return `${compType}_${path.join('.')}`;
  };

  constructor(private clientSpecMapService: ClientSpecMapService) {}

  getAll() {
    return Object.fromEntries(this.#store.entries());
  }

  hasPanelFactory(compType: string, actionName: string) {
    if (!this.#store.has(compType)) {
      return false;
    }

    const panels = this.#store.get(compType)!;
    return actionName in panels && Boolean(panels[actionName]);
  }

  getPanelFactory(compType: string, actionName: string) {
    return this.#store.get(compType)?.[actionName];
  }

  setPanel(compType: string, actionName: string, panel: () => React.ReactNode) {
    const panels = this.#store.get(compType) ?? {};

    this.#store.set(compType, {
      ...panels,
      [actionName]: panel,
    });
  }

  hasPanelByEditorPointer(
    editorPointer: EditorPointer<CompRef>,
    actionName: string,
  ) {
    return this.hasPanelFactory(this.#getCompType(editorPointer), actionName);
  }

  getPanelByEditorPointer(
    editorPointer: EditorPointer<CompRef>,
    actionName: string,
  ) {
    const panelFactory = this.getPanelFactory(
      this.#getCompType(editorPointer),
      actionName,
    );

    if (!panelFactory) {
      return null;
    }

    return panelFactory(editorPointer);
  }

  #getCompType(editorPointer: EditorPointer<CompRef>) {
    const elementPath = editorPointer.innerElementPath ?? [];
    const compType = this.clientSpecMapService.getCompType(editorPointer);

    if (elementPath.length) {
      return PanelStore.getPanelId(compType, elementPath);
    }

    return compType;
  }
}
